import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import {
  Rss,
  Star,
  Search,
  List,
  Columns3,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  ArrowDown,
  ArrowUp,
  ExternalLink,
  Settings2,
  Plus,
  Loader2,
  ArrowDownAZ,
  ArrowUpAZ,
  MoreHorizontal,
  Pencil,
  Archive,
  Sparkles,
} from 'lucide-react';
import { TwitterIcon, InstagramIcon } from '../components/PlatformIcons';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { socialMediaApi } from '../api';
import type {
  UnifiedContentItem,
  ContentStatus,
  InspirationPlatform,
  SocialMediaCompetitorItem,
  SocialMediaRssFeedItem,
} from '@/lib/api';
import { ContentSourcesModal } from '../components/ContentSourcesModal';
import { useBrandSettings } from '../hooks';
import { useHolderSwitcher } from '@/components/HolderSwitcher';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 30;

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  twitter: <TwitterIcon className="h-3.5 w-3.5" />,
  instagram: <InstagramIcon className="h-3.5 w-3.5" />,
  tiktok: (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.54V6.79a4.85 4.85 0 01-1.02-.1z" />
    </svg>
  ),
  rss: <Rss className="h-3.5 w-3.5" />,
  manual: <Pencil className="h-3.5 w-3.5" />,
};

const STATUS_CLASSES: Record<ContentStatus, string> = {
  new: 'bg-blue-50 text-blue-600',
  reviewed: 'bg-[#F0F0F0] text-[#666666]',
  saved: 'bg-[#F0EEFF] text-[#6B4EFF]',
  used: 'bg-[#EDFAF3] text-[#27A865]',
  discarded: 'bg-[#FEF2F2] text-[#DC2626]',
};

const STATUS_LABELS: Record<ContentStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  saved: 'Saved',
  used: 'Used',
  discarded: 'Discarded',
};

const SCORE_TYPE_LABELS: Record<string, string> = {
  full: 'Full',
  partial: 'Partial',
  estimated: 'Est.',
};

const SCORE_TYPE_CLASSES: Record<string, string> = {
  full: 'text-green-600',
  partial: 'text-yellow-600',
  estimated: 'text-muted-foreground',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatNumber(n: number | null): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ── Pill ──────────────────────────────────────────────────────────────────────

function Pill({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium leading-tight whitespace-nowrap',
        className,
      )}
    >
      {children}
    </span>
  );
}

// ── BulkActionBar ─────────────────────────────────────────────────────────────

type Placement = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

type PillarPickerProps = {
  pillars: string[];
  onBulkAction: (
    action: 'tag' | 'status' | 'favorite' | 'unfavorite' | 'discard',
    opts?:
      | {
          status?: ContentStatus | undefined;
          pillarTag: string;
        }
      | undefined,
  ) => void;
  isLoading: boolean;
};

export const PillarPicker = ({ pillars, onBulkAction, isLoading }: PillarPickerProps) => {
  const [selectedPillar, setSelectedPillar] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [placement, setPlacement] = useState<Placement>('bottom-left');

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    setCoords({
      x: rect.left,
      y: rect.bottom,
    });

    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open || !panelRef.current) return;

    const panel = panelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const overflowRight = coords.x + panel.width > vw;
    const overflowBottom = coords.y + panel.height > vh;

    if (overflowRight && overflowBottom) setPlacement('top-left');
    else if (overflowRight) setPlacement('bottom-left');
    else if (overflowBottom) setPlacement('top-right');
    else setPlacement('bottom-right');
  }, [open, coords]);

  const getStyle = (): React.CSSProperties => {
    const offset = 8;

    switch (placement) {
      case 'bottom-right':
        return {
          position: 'fixed',
          top: coords.y + offset,
          left: coords.x,
        };
      case 'bottom-left':
        return {
          position: 'fixed',
          top: coords.y + offset,
          left: coords.x,
          transform: 'translateX(-100%)',
        };
      case 'top-right':
        return {
          position: 'fixed',
          top: coords.y,
          left: coords.x,
          transform: 'translateY(-100%)',
        };
      case 'top-left':
        return {
          position: 'fixed',
          top: coords.y,
          left: coords.x,
          transform: 'translate(-100%, -100%)',
        };
    }
  };

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={isLoading || pillars.length === 0}
        className="h-7 px-2 text-[10px] font-mono border rounded"
      >
        Tag
      </button>

      {open && (
        <div
          ref={panelRef}
          style={getStyle()}
          className="z-[1000] w-[200px] rounded-xl border bg-background shadow-md"
        >
          <div className="p-2">
            {pillars.map((pillar: string) => (
              <label
                key={pillar}
                className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-[#F6F6F7] cursor-pointer"
              >
                <input
                  type="radio"
                  name="bulk-pillar"
                  checked={selectedPillar === pillar}
                  onChange={() => setSelectedPillar(pillar)}
                />
                <span className="text-[13px]">{pillar}</span>
              </label>
            ))}
          </div>

          <div className="border-t px-3 py-2 flex justify-between">
            <button onClick={() => setSelectedPillar(null)}>Clear</button>

            <button
              disabled={!selectedPillar}
              onClick={() => {
                if (!selectedPillar) return;

                onBulkAction('tag', { pillarTag: selectedPillar });
                setOpen(false);
                setSelectedPillar(null);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </>
  );
};

interface BulkActionBarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkAction: (
    action: 'status' | 'favorite' | 'unfavorite' | 'tag' | 'discard',
    opts?: { status?: ContentStatus; pillarTag?: string },
  ) => void;
  isLoading: boolean;
  pillars: string[];
}

function BulkActionBar({
  selectedCount,
  onClearSelection,
  onBulkAction,
  isLoading,
  pillars,
}: BulkActionBarProps) {
  const [showPillarPicker, setShowPillarPicker] = useState(false);
  const tagContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showPillarPicker) return;
    function handleClickOutside(e: MouseEvent) {
      if (tagContainerRef.current && !tagContainerRef.current.contains(e.target as Node)) {
        setShowPillarPicker(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPillarPicker]);

  return (
    <div className="flex flex-wrap justify-between gap-2 px-3 sm:px-4 py-2 bg-background shadow-social-card rounded-xl">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-primary font-satoshi">
          {selectedCount} selected
        </span>

        <button
          onClick={onClearSelection}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors font-mono"
        >
          Clear
        </button>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5 text-[10px] font-normal font-mono !px-2 !py-0 sm:shrink-0"
          disabled={isLoading}
          onClick={() => onBulkAction('status', { status: 'saved' })}
        >
          Save selected
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5 text-[10px] font-normal font-mono !px-2 !py-0 sm:shrink-0"
          disabled={isLoading}
          onClick={() => onBulkAction('status', { status: 'used' })}
        >
          Generate Posts
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2.5 text-[10px] font-normal font-mono !px-2 !py-0 sm:shrink-0"
          disabled={isLoading}
          onClick={() => onBulkAction('discard')}
        >
          Discard
        </Button>

        {/* Pillar tag picker */}
        <PillarPicker pillars={pillars} onBulkAction={onBulkAction} isLoading={isLoading} />
      </div>

      {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
    </div>
  );
}

// ── ListView ──────────────────────────────────────────────────────────────────

interface ListViewProps {
  items: UnifiedContentItem[];
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onUpdateItem: (
    contentId: string,
    update: { status?: ContentStatus; isFavorited?: boolean; pillarTag?: string | null },
  ) => void;
  onGeneratePost: (item: UnifiedContentItem) => void;
  sortDir: 'asc' | 'desc';
  onToggleSortDir: () => void;
}

// ── RowActionsMenu ─────────────────────────────────────────────────────────────

interface RowActionsMenuProps {
  item: UnifiedContentItem;
  onUpdateItem: (id: string, update: { status?: ContentStatus; isFavorited?: boolean }) => void;
  onGeneratePost: (item: UnifiedContentItem) => void;
}

function RowActionsMenu({ item, onUpdateItem, onGeneratePost }: RowActionsMenuProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [placement, setPlacement] = useState<Placement>('bottom-left');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    setCoords({ x: rect.left, y: rect.bottom });
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const panel = panelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const overflowRight = coords.x + panel.width > vw;
    const overflowBottom = coords.y + panel.height > vh;
    if (overflowRight && overflowBottom) setPlacement('top-left');
    else if (overflowRight) setPlacement('bottom-left');
    else if (overflowBottom) setPlacement('top-right');
    else setPlacement('bottom-right');
  }, [open, coords]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const getStyle = (): React.CSSProperties => {
    const offset = 8;
    switch (placement) {
      case 'bottom-right':
        return { position: 'fixed', top: coords.y + offset, left: coords.x };
      case 'bottom-left':
        return {
          position: 'fixed',
          top: coords.y + offset,
          left: coords.x,
          transform: 'translateX(-100%)',
        };
      case 'top-right':
        return { position: 'fixed', top: coords.y, left: coords.x, transform: 'translateY(-100%)' };
      case 'top-left':
        return {
          position: 'fixed',
          top: coords.y,
          left: coords.x,
          transform: 'translate(-100%, -100%)',
        };
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Row actions"
      >
        <MoreHorizontal className="h-3.5 w-3.5" />
      </button>

      {open && (
        <div
          ref={panelRef}
          style={getStyle()}
          className="z-[1000] w-[190px] rounded-xl border bg-background shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-1.5">
            <button
              onClick={() => {
                onGeneratePost(item);
                setOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
              Generate post
            </button>
            {item.status === 'used' && item.draftPostId && (
              <button
                onClick={() => {
                  navigate(`/a/arche_social_media/compose/${item.draftPostId}`);
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left"
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                View in composer
              </button>
            )}
            <button
              onClick={() => {
                onUpdateItem(item.contentId, { status: 'discarded' });
                setOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left text-destructive"
            >
              <Archive className="h-3.5 w-3.5 shrink-0" />
              Archive
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function ListView({
  items,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onUpdateItem,
  onGeneratePost,
  sortDir,
  onToggleSortDir,
}: ListViewProps) {
  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.contentId));

  return (
    <div className="max-w-full w-full relative flex flex-col gap-1">
      <div className="relative max-w-full w-full rounded-[10px]">
        <div
          className={cn(
            'relative z-0 w-full overflow-visible',
            "before:content-[''] before:absolute before:left-0 before:right-0 before:top-[44px] before:bottom-0",
            'before:rounded-3xl before:bg-background',
            'before:shadow-social-list before:pointer-events-none before:-z-10',
          )}
        >
          <div className="relative z-10 w-full overflow-x-auto overflow-y-visible">
            <table className="w-full border-collapse text-sm text-left" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  {/* Checkbox */}
                  <th className="py-3 pl-4 pr-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onSelectAll}
                      className="rounded border-border"
                      aria-label="Select all"
                    />
                  </th>

                  {/* Content */}
                  <th className="py-3 px-4">
                    <button
                      onClick={onToggleSortDir}
                      className="flex items-center gap-1 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase hover:text-[#555] transition-colors"
                    >
                      Content
                      {sortDir === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : (
                        <ArrowDown className="h-3 w-3" />
                      )}
                    </button>
                  </th>

                  {/* Platform */}
                  <th className="py-3 px-4 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase whitespace-nowrap">
                    Platform
                  </th>

                  {/* Engagement */}
                  <th className="py-3 px-4 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase whitespace-nowrap">
                    Engagement
                  </th>

                  {/* Status */}
                  <th className="py-3 px-4 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase whitespace-nowrap">
                    Status
                  </th>

                  {/* Tag (pillar) */}
                  <th className="py-3 px-4 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase whitespace-nowrap">
                    Tag
                  </th>

                  {/* Fetched */}
                  <th className="py-3 px-4 text-[11px] font-[400] tracking-[0.05em] text-[#9A9A9A] uppercase whitespace-nowrap">
                    Fetched
                  </th>

                  {/* Actions */}
                  <th className="py-3 px-4 w-10" />
                </tr>
              </thead>

              <tbody className="relative w-full">
                {items.map((item, i) => {
                  const isFirst = i === 0;
                  const isLast = i === items.length - 1;
                  const isSelected = selectedIds.has(item.contentId);

                  return (
                    <tr
                      key={item.contentId}
                      className={cn(
                        'transition bg-background',
                        !isLast && 'border-b border-dashed border-b-[rgba(0,0,0,0.08)]',
                        isFirst &&
                          '[&>td:first-child]:rounded-tl-3xl [&>td:last-child]:rounded-tr-3xl',
                        isLast &&
                          '[&>td:first-child]:rounded-bl-3xl [&>td:last-child]:rounded-br-3xl',
                        isSelected && 'bg-primary/[0.03]',
                      )}
                    >
                      {/* Checkbox */}
                      <td className="pl-4 pr-2 py-4 align-middle w-8">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => onToggleSelect(item.contentId)}
                          className="rounded border-border"
                          aria-label={`Select ${item.author}`}
                        />
                      </td>

                      {/* Content — star + author + date + preview */}
                      <td className="px-4 py-4 align-middle">
                        <div className="flex items-start gap-2 min-w-0 max-w-[340px]">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 min-w-0 mb-2">
                              {item.mediaUrl && (
                                <img
                                  src={item.mediaUrl}
                                  alt=""
                                  className="w-7 h-7 rounded object-cover shrink-0"
                                />
                              )}
                              <p className="text-[13px] font-[600] text-[#1A1A1A] truncate">
                                {item.author}
                                <span className="ml-2 text-[11px] text-[#9A9A9A] font-[400]">
                                  {relativeTime(item.postedAt)}
                                </span>
                              </p>
                              {/* Favorite star */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onUpdateItem(item.contentId, { isFavorited: !item.isFavorited });
                                }}
                                className={cn(
                                  'shrink-0 mt-0.5 transition-colors',
                                  item.isFavorited
                                    ? 'text-yellow-400'
                                    : 'text-muted-foreground hover:text-yellow-400',
                                )}
                                aria-label={item.isFavorited ? 'Unfavorite' : 'Favorite'}
                              >
                                <Star
                                  className={cn('h-3.5 w-3.5', item.isFavorited && 'fill-current')}
                                />
                              </button>
                            </div>
                            <p className="text-[13px] text-[#444] line-clamp-2 leading-snug mt-0.5">
                              {item.contentPreview || (
                                <span className="italic text-[#9A9A9A]">No preview</span>
                              )}
                            </p>
                            {item.externalUrl && (
                              <a
                                href={item.externalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 mt-1 text-[11px] text-primary hover:underline"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <ExternalLink className="h-2.5 w-2.5" />
                                View original
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Platform */}
                      <td className="px-4 py-4 align-middle">
                        <span className="flex items-center gap-1.5 text-[#444]">
                          {PLATFORM_ICONS[item.platform] ?? item.platform}
                        </span>
                      </td>

                      {/* Engagement */}
                      <td className="px-4 py-4 align-middle whitespace-nowrap">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2 text-[12px] text-[#444]">
                            {item.likes !== null && <span>♥ {formatNumber(item.likes)}</span>}
                            {item.comments !== null && (
                              <span>💬 {formatNumber(item.comments)}</span>
                            )}
                            {item.shares !== null && <span>↗ {formatNumber(item.shares)}</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="text-[11px] text-[#9A9A9A]">
                              {item.engagementScore.toFixed(0)} pts
                            </span>
                            <span
                              className={cn(
                                'text-[10px] font-medium',
                                SCORE_TYPE_CLASSES[item.scoreType],
                              )}
                            >
                              {SCORE_TYPE_LABELS[item.scoreType]}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-4 align-middle">
                        <Pill className={STATUS_CLASSES[item.status]}>
                          {STATUS_LABELS[item.status]}
                        </Pill>
                      </td>

                      {/* Tag (pillar) */}
                      <td className="px-4 py-4 align-middle">
                        {item.pillarTag ? (
                          <Pill className="bg-[#F0F0F0] text-[#666666] text-[11px] px-2 py-0">
                            {item.pillarTag}
                          </Pill>
                        ) : (
                          <span className="text-[12px] text-[#9A9A9A]">—</span>
                        )}
                      </td>

                      {/* Fetched */}
                      <td className="px-4 py-4 align-middle whitespace-nowrap text-[12px] text-[#9A9A9A]">
                        {relativeTime(item.fetchedAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-4 align-middle">
                        <RowActionsMenu
                          item={item}
                          onUpdateItem={onUpdateItem}
                          onGeneratePost={onGeneratePost}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── KanbanColumn ──────────────────────────────────────────────────────────────

const KANBAN_COLUMNS: { status: ContentStatus; label: string; color: string }[] = [
  { status: 'new', label: 'New', color: 'bg-blue-500' },
  { status: 'reviewed', label: 'Reviewed', color: 'bg-gray-400' },
  { status: 'saved', label: 'Saved', color: 'bg-violet-500' },
  { status: 'used', label: 'Used', color: 'bg-green-500' },
];

interface KanbanCardProps {
  item: UnifiedContentItem;
  onUpdateItem: (id: string, update: { status?: ContentStatus; isFavorited?: boolean }) => void;
  onGeneratePost: (item: UnifiedContentItem) => void;
}

function KanbanCard({ item, onUpdateItem, onGeneratePost }: KanbanCardProps) {
  const navigate = useNavigate();
  const [actionsOpen, setActionsOpen] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [placement, setPlacement] = useState<Placement>('bottom-left');
  const actionsBtnRef = useRef<HTMLButtonElement>(null);
  const actionsPanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!actionsOpen || !actionsPanelRef.current) return;
    const panel = actionsPanelRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const overflowRight = coords.x + panel.width > vw;
    const overflowBottom = coords.y + panel.height > vh;
    if (overflowRight && overflowBottom) setPlacement('top-left');
    else if (overflowRight) setPlacement('bottom-left');
    else if (overflowBottom) setPlacement('top-right');
    else setPlacement('bottom-right');
  }, [actionsOpen, coords]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        !actionsPanelRef.current?.contains(e.target as Node) &&
        !actionsBtnRef.current?.contains(e.target as Node)
      ) {
        setActionsOpen(false);
      }
    };
    if (actionsOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [actionsOpen]);

  const getActionsStyle = (): React.CSSProperties => {
    const offset = 8;
    switch (placement) {
      case 'bottom-right':
        return { position: 'fixed', top: coords.y + offset, left: coords.x };
      case 'bottom-left':
        return {
          position: 'fixed',
          top: coords.y + offset,
          left: coords.x,
          transform: 'translateX(-100%)',
        };
      case 'top-right':
        return { position: 'fixed', top: coords.y, left: coords.x, transform: 'translateY(-100%)' };
      case 'top-left':
        return {
          position: 'fixed',
          top: coords.y,
          left: coords.x,
          transform: 'translate(-100%, -100%)',
        };
    }
  };

  return (
    <div className="bg-background rounded-2xl border border-[rgba(0,0,0,0.08)] p-3 shadow-sm space-y-2 group">
      {/* Author + platform + favorite + actions */}
      <div className="flex items-center gap-1.5 min-w-0">
        <span className="text-muted-foreground shrink-0">{PLATFORM_ICONS[item.platform]}</span>
        <p className="text-[12px] font-semibold text-[#1A1A1A] truncate flex-1">{item.author}</p>
        <button
          onClick={() => onUpdateItem(item.contentId, { isFavorited: !item.isFavorited })}
          className={cn(
            'shrink-0 transition-colors',
            item.isFavorited ? 'text-yellow-400' : 'text-muted-foreground hover:text-yellow-400',
          )}
          aria-label={item.isFavorited ? 'Unfavorite' : 'Favorite'}
        >
          <Star className={cn('h-3.5 w-3.5', item.isFavorited && 'fill-current')} />
        </button>
        {/* 3-dot actions */}
        <button
          ref={actionsBtnRef}
          onClick={(e) => {
            e.stopPropagation();
            if (!actionsBtnRef.current) return;
            const rect = actionsBtnRef.current.getBoundingClientRect();
            setCoords({ x: rect.left, y: rect.bottom });
            setActionsOpen((v) => !v);
          }}
          className="shrink-0 p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover:opacity-100"
          aria-label="Card actions"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Content preview */}
      <p className="text-[12px] text-[#444] line-clamp-3 leading-snug">{item.contentPreview}</p>

      {/* Engagement */}
      {(item.likes !== null || item.comments !== null) && (
        <div className="flex items-center gap-2 text-[11px] text-[#9A9A9A]">
          {item.likes !== null && <span>♥ {formatNumber(item.likes)}</span>}
          {item.comments !== null && <span>💬 {formatNumber(item.comments)}</span>}
          {item.shares !== null && <span>↗ {formatNumber(item.shares)}</span>}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-[#9A9A9A]">{relativeTime(item.postedAt)}</span>
        <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {item.status !== 'used' && (
            <button
              onClick={() => {
                const next: Record<string, ContentStatus> = {
                  new: 'reviewed',
                  reviewed: 'saved',
                  saved: 'used',
                };
                const nextStatus = next[item.status];
                if (nextStatus) onUpdateItem(item.contentId, { status: nextStatus });
              }}
              className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
            >
              Move →
            </button>
          )}
          {item.externalUrl && (
            <a
              href={item.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-primary transition-colors"
              aria-label="View original"
            >
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      {/* Actions popover */}
      {actionsOpen && (
        <div
          ref={actionsPanelRef}
          style={getActionsStyle()}
          className="z-[1000] w-[190px] rounded-xl border bg-background shadow-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-1.5">
            <button
              onClick={() => {
                onGeneratePost(item);
                setActionsOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left"
            >
              <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
              Generate post
            </button>
            {item.status === 'used' && item.draftPostId && (
              <button
                onClick={() => {
                  navigate(`/a/arche_social_media/compose/${item.draftPostId}`);
                  setActionsOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left"
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                View in composer
              </button>
            )}
            <button
              onClick={() => {
                onUpdateItem(item.contentId, { status: 'discarded' });
                setActionsOpen(false);
              }}
              className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] hover:bg-[#F6F6F7] transition-colors text-left text-destructive"
            >
              <Archive className="h-3.5 w-3.5 shrink-0" />
              Archive
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

interface KanbanViewProps {
  items: UnifiedContentItem[];
  onUpdateItem: (id: string, update: { status?: ContentStatus; isFavorited?: boolean }) => void;
  onGeneratePost: (item: UnifiedContentItem) => void;
}

function KanbanView({ items, onUpdateItem, onGeneratePost }: KanbanViewProps) {
  const byStatus = (status: ContentStatus) => items.filter((i) => i.status === status);

  return (
    <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: 480 }}>
      {KANBAN_COLUMNS.map(({ status, label, color }) => {
        const columnItems = byStatus(status);
        return (
          <div key={status} className="flex-1 min-w-[220px] max-w-[300px] flex flex-col gap-3">
            {/* Column header */}
            <div className="flex items-center gap-2 px-1">
              <span className={cn('w-2 h-2 rounded-full shrink-0', color)} />
              <span className="text-xs font-semibold text-[#1A1A1A]">{label}</span>
              <span className="ml-auto text-[11px] text-[#9A9A9A] tabular-nums">
                {columnItems.length}
              </span>
            </div>
            {/* Cards */}
            <div className="flex flex-col gap-2 flex-1">
              {columnItems.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-border flex items-center justify-center h-20 text-[11px] text-muted-foreground">
                  Empty
                </div>
              ) : (
                columnItems.map((item) => (
                  <KanbanCard
                    key={item.contentId}
                    item={item}
                    onUpdateItem={onUpdateItem}
                    onGeneratePost={onGeneratePost}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── InspirationPage ───────────────────────────────────────────────────────────

type ViewMode = 'list' | 'kanban';
type SortBy = 'engagement' | 'fetched' | 'posted';
type FilterType = 'platform' | 'status';

const PLATFORM_OPTIONS: { label: string; value: InspirationPlatform }[] = [
  { label: 'Twitter', value: 'twitter' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'RSS', value: 'rss' },
];

const STATUS_OPTIONS: { label: string; value: ContentStatus }[] = [
  { label: 'New', value: 'new' },
  { label: 'Reviewed', value: 'reviewed' },
  { label: 'Saved', value: 'saved' },
  { label: 'Used', value: 'used' },
  { label: 'Discarded', value: 'discarded' },
];

const SORT_BY_OPTIONS: { label: string; value: SortBy }[] = [
  { label: 'Date Fetched', value: 'fetched' },
  { label: 'Date Posted', value: 'posted' },
  { label: 'Engagement Score', value: 'engagement' },
];

// ── FilterChip ────────────────────────────────────────────────────────────────

interface FilterChipProps {
  label: string;
  values: string[];
  onRemove: () => void;
}

function FilterChip({ label, values, onRemove }: FilterChipProps) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#F1F2F3] border border-[#E1E3E5] px-3 h-8 text-[13px] text-foreground shrink-0">
      <span className="text-[#888]">{label}:</span>
      <span className="font-medium">{values.join(', ')}</span>
      <button
        onClick={onRemove}
        className="ml-0.5 text-[#888] hover:text-foreground transition-colors text-base leading-none"
        aria-label={`Remove ${label} filter`}
      >
        ×
      </button>
    </span>
  );
}

// ── SortDropdown ──────────────────────────────────────────────────────────────

interface SortDropdownProps {
  sortBy: SortBy;
  sortDir: 'asc' | 'desc';
  onChangeSortBy: (v: SortBy) => void;
  onChangeSortDir: (v: 'asc' | 'desc') => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

function SortDropdown({
  sortBy,
  sortDir,
  onChangeSortBy,
  onChangeSortDir,
  onClose,
  anchorRef,
}: SortDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 z-50 w-[220px] rounded-xl border border-[#E1E3E5] bg-background shadow-md"
    >
      <div className="p-3">
        <p className="text-[11px] font-medium text-[#9A9A9A] uppercase tracking-wide mb-1.5">
          Sort By
        </p>
        {SORT_BY_OPTIONS.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#F6F6F7] cursor-pointer transition-colors"
          >
            <input
              type="radio"
              name="sort-by"
              value={opt.value}
              checked={sortBy === opt.value}
              onChange={() => onChangeSortBy(opt.value)}
              className="w-4 h-4 text-primary cursor-pointer"
            />
            <span className="text-[13px] text-foreground">{opt.label}</span>
          </label>
        ))}
      </div>
      <div className="border-t border-[#E1E3E5] p-3">
        <p className="text-[11px] font-medium text-[#9A9A9A] uppercase tracking-wide mb-1.5">
          Direction
        </p>
        <div className="flex flex-col gap-1">
          {(['desc', 'asc'] as const).map((dir) => (
            <label
              key={dir}
              onClick={() => onChangeSortDir(dir)}
              className={cn(
                'flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#F6F6F7] cursor-pointer transition-colors',
                sortDir === dir ? 'bg-[#F6F6F7]' : '',
              )}
            >
              {dir !== 'asc' ? (
                <ArrowDownAZ className="h-4 w-4" />
              ) : (
                <ArrowUpAZ className="h-4 w-4" />
              )}
              <span className="text-[13px] text-foreground">
                {dir === 'asc' ? 'Ascending' : 'Descending'}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── FilterOptionsPanel ────────────────────────────────────────────────────────

interface FilterOptionsPanelProps {
  type: FilterType;
  selectedPlatforms: Set<InspirationPlatform>;
  selectedStatuses: Set<ContentStatus>;
  onTogglePlatform: (v: InspirationPlatform) => void;
  onToggleStatus: (v: ContentStatus) => void;
  onClear: () => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
}

function FilterOptionsPanel({
  type,
  selectedPlatforms,
  selectedStatuses,
  onTogglePlatform,
  onToggleStatus,
  onClear,
  onClose,
  anchorRef,
}: FilterOptionsPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  const isPlatform = type === 'platform';
  const options = isPlatform
    ? PLATFORM_OPTIONS.map((o) => ({
        label: o.label,
        value: o.value,
        checked: selectedPlatforms.has(o.value),
        onToggle: () => onTogglePlatform(o.value),
      }))
    : STATUS_OPTIONS.map((o) => ({
        label: o.label,
        value: o.value,
        checked: selectedStatuses.has(o.value),
        onToggle: () => onToggleStatus(o.value),
      }));

  const hasSelection = isPlatform ? selectedPlatforms.size > 0 : selectedStatuses.size > 0;

  return (
    <div
      ref={panelRef}
      className="absolute left-0 top-full mt-2 z-50 w-[260px] rounded-xl border border-border bg-background shadow-md"
    >
      <div className="p-3">
        <p className="text-[11px] font-medium text-[#9A9A9A] uppercase tracking-wide mb-1.5">
          {isPlatform ? 'Platform' : 'Status'}
        </p>
        {options.map((opt) => (
          <label
            key={opt.value}
            className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-[#F6F6F7] cursor-pointer transition-colors"
          >
            <input
              radioGroup="isPlatform"
              type={isPlatform ? 'checkbox' : 'radio'}
              checked={opt.checked}
              onChange={opt.onToggle}
              className="w-4 h-4 rounded border-[#E1E3E5] text-primary cursor-pointer"
            />
            <span className="text-[13px] text-foreground">{opt.label}</span>
          </label>
        ))}
      </div>
      {hasSelection && (
        <div className="border-t border-[#E1E3E5] px-4 py-2.5">
          <button
            onClick={onClear}
            className="text-[13px] text-[#9A9A9A] hover:text-foreground transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

// ── AddFilterDropdown ─────────────────────────────────────────────────────────

interface AddFilterDropdownProps {
  onSelectType: (type: FilterType) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  activeTypes: Set<FilterType>;
}

function AddFilterDropdown({
  onSelectType,
  onClose,
  anchorRef,
  activeTypes,
}: AddFilterDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, anchorRef]);

  return (
    <div
      ref={panelRef}
      className="absolute left-0 top-full mt-2 z-50 w-[180px] rounded-xl border border-border bg-background shadow-md"
    >
      <div className="p-2">
        {(['platform', 'status'] as FilterType[]).map((type) => (
          <button
            key={type}
            onClick={() => onSelectType(type)}
            className="w-full text-left px-3 py-2 rounded-lg text-[13px] text-foreground hover:bg-[#F6F6F7] transition-colors flex items-center justify-between"
          >
            <span>{type === 'platform' ? 'Platform' : 'Status'}</span>
            {activeTypes.has(type) && (
              <span className="text-[11px] text-primary font-medium">active</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function InspirationPage() {
  const navigate = useNavigate();
  const { selectedHolderId } = useHolderSwitcher();
  const { settings: brandSettings } = useBrandSettings(selectedHolderId);

  const pillars = (brandSettings?.voice?.contentPillars ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [platformFilter, setPlatformFilter] = useState<Set<InspirationPlatform>>(new Set());
  const [statusFilter, setStatusFilter] = useState<Set<ContentStatus>>(new Set());
  const [sortBy, setSortBy] = useState<SortBy>('fetched');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);

  const [sortOpen, setSortOpen] = useState(false);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [filterPanelType, setFilterPanelType] = useState<FilterType | null>(null);

  const [items, setItems] = useState<UnifiedContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const [showSourcesModal, setShowSourcesModal] = useState(false);
  const [competitors, setCompetitors] = useState<SocialMediaCompetitorItem[]>([]);
  const [rssFeeds, setRssFeeds] = useState<SocialMediaRssFeedItem[]>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sortBtnRef = useRef<HTMLButtonElement>(null);
  const addFilterBtnRef = useRef<HTMLButtonElement>(null);

  // ── Fetch content ──────────────────────────────────────────────────────────

  const fetchContent = useCallback(async () => {
    if (!selectedHolderId) {
      setItems([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await socialMediaApi.listInspirationContent(selectedHolderId, {
        platform: platformFilter.size === 1 ? [...platformFilter][0] : undefined,
        status: statusFilter.size === 1 ? [...statusFilter][0] : undefined,
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load content');
    } finally {
      setIsLoading(false);
    }
  }, [selectedHolderId, platformFilter, statusFilter, debouncedSearch, sortBy, sortDir, page]);

  useEffect(() => {
    void fetchContent();
  }, [fetchContent]);

  // Reset to page 0 when filters/search change
  useEffect(() => {
    setPage(0);
  }, [platformFilter, statusFilter, debouncedSearch, sortBy, sortDir]);

  // ── Search debounce ────────────────────────────────────────────────────────

  function handleSearchChange(value: string) {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  }

  // ── Sources (for modal) ────────────────────────────────────────────────────

  const fetchSources = useCallback(async () => {
    if (!selectedHolderId) {
      setCompetitors([]);
      setRssFeeds([]);
      return;
    }
    try {
      const [compResult, rssResult] = await Promise.all([
        socialMediaApi.listCompetitors(selectedHolderId),
        socialMediaApi.listRssFeeds(selectedHolderId),
      ]);
      setCompetitors(compResult.competitors);
      setRssFeeds(rssResult.feeds);
    } catch {
      /* non-critical */
    }
  }, [selectedHolderId]);

  useEffect(() => {
    if (showSourcesModal) void fetchSources();
  }, [showSourcesModal, fetchSources]);

  // ── Selection ──────────────────────────────────────────────────────────────

  function handleToggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelectAll() {
    if (items.every((i) => selectedIds.has(i.contentId))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map((i) => i.contentId)));
    }
  }

  // ── Single item update ─────────────────────────────────────────────────────

  const handleUpdateItem = useCallback(
    async (
      contentId: string,
      update: { status?: ContentStatus; isFavorited?: boolean; pillarTag?: string | null },
    ) => {
      if (!selectedHolderId) return;
      // Optimistic update
      setItems((prev) =>
        prev.map((item) => (item.contentId === contentId ? { ...item, ...update } : item)),
      );
      try {
        await socialMediaApi.updateInspirationContent(selectedHolderId, contentId, update);
      } catch {
        // Revert on failure by re-fetching
        void fetchContent();
      }
    },
    [selectedHolderId, fetchContent, items],
  );

  // ── Generate post ─────────────────────────────────────────────────────────

  const handleGeneratePost = useCallback(
    async (item: UnifiedContentItem) => {
      if (!selectedHolderId) return;
      try {
        const { postId } = await socialMediaApi.generatePostDraft(selectedHolderId, item.contentId);
        // Refresh so the item's draftPostId is up to date in local state
        void fetchContent();
        navigate(`/a/arche_social_media/compose/${postId}`);
      } catch (err) {
        toast({
          title: 'Failed to create draft',
          description: err instanceof Error ? err.message : 'Please try again.',
          variant: 'destructive',
        });
      }
    },
    [selectedHolderId, navigate, fetchContent],
  );

  // ── Bulk action ────────────────────────────────────────────────────────────

  const handleBulkAction = useCallback(
    async (
      action: 'status' | 'favorite' | 'unfavorite' | 'tag' | 'discard',
      opts?: { status?: ContentStatus; pillarTag?: string },
    ) => {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      if (!selectedHolderId) return;

      setBulkLoading(true);
      try {
        await socialMediaApi.bulkUpdateInspirationContent(selectedHolderId, {
          contentIds: ids,
          action,
          status: opts?.status,
          pillarTag: opts?.pillarTag ?? null,
        });

        // Optimistically remove discarded items from the current view
        if (action === 'discard') {
          setItems((prev) => prev.filter((item) => !selectedIds.has(item.contentId)));
        }

        const count = ids.length;
        const actionLabels: Record<string, string> = {
          status: `Marked ${count} item${count !== 1 ? 's' : ''} as ${opts?.status ?? 'updated'}`,
          favorite: `Favorited ${count} item${count !== 1 ? 's' : ''}`,
          unfavorite: `Unfavorited ${count} item${count !== 1 ? 's' : ''}`,
          discard: `Discarded ${count} item${count !== 1 ? 's' : ''}`,
          tag: `Tagged ${count} item${count !== 1 ? 's' : ''}`,
        };
        toast({ title: actionLabels[action] ?? 'Done' });

        setSelectedIds(new Set());
        void fetchContent();
      } catch {
        toast({ title: 'Action failed', description: 'Please try again.', variant: 'destructive' });
      } finally {
        setBulkLoading(false);
      }
    },
    [selectedIds, fetchContent, items],
  );

  // ── Filter helpers ─────────────────────────────────────────────────────────

  function togglePlatform(value: InspirationPlatform) {
    setPlatformFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function toggleStatus(value: ContentStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  const activeFilterTypes = new Set<FilterType>([
    ...(platformFilter.size > 0 ? (['platform'] as FilterType[]) : []),
    ...(statusFilter.size > 0 ? (['status'] as FilterType[]) : []),
  ]);

  // ── Pagination ─────────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const lastItem = Math.min((page + 1) * PAGE_SIZE, total);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-3 sm:p-6 space-y-3 bg-social-bg min-h-full pb-10">
      {/* Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* View toggle */}
        <div className="flex overflow-hidden shrink-0 gap-1">
          <button
            className={cn(
              'p-2 transition-colors rounded-md cursor-pointer',
              viewMode === 'list'
                ? 'text-foreground bg-muted'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            )}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            className={cn(
              'p-2 transition-colors rounded-md cursor-pointer',
              viewMode === 'kanban'
                ? 'text-foreground bg-muted'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            )}
            onClick={() => setViewMode('kanban')}
            title="Kanban view"
          >
            <Columns3 className="h-4 w-4" />
          </button>
        </div>

        <div className="relative flex flex-1 items-center justify-end gap-2 min-w-0">
          {/* Search */}
          <div className="relative min-w-auto">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9A9A9A] pointer-events-none" />
            <input
              type="text"
              placeholder="Searching in all"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              className={cn(
                'w-full h-9 pl-9 pr-3 text-sm bg-background border border-[#E1E3E5] rounded-lg placeholder:text-[#9A9A9A] focus:outline-none focus:border-primary/50 focus:bg-background transition-colors',
                '!pl-8 w-full text-[12px] rounded-[10px] outline-none transition-all duration-150 shadow-social-card font-mono',
              )}
            />
          </div>

          {/* Sort */}
          <div className="relative shrink-0">
            <button
              ref={sortBtnRef}
              onClick={() => {
                setSortOpen((v) => !v);
                setAddFilterOpen(false);
                setFilterPanelType(null);
              }}
              className={cn(
                'flex items-center justify-center w-9 h-9 rounded-lg border transition-colors',
                sortOpen
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-background text-foreground border-border hover:bg-muted/50',
              )}
              title="Sort"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </button>
            {sortOpen && (
              <SortDropdown
                sortBy={sortBy}
                sortDir={sortDir}
                onChangeSortBy={setSortBy}
                onChangeSortDir={setSortDir}
                onClose={() => setSortOpen(false)}
                anchorRef={sortBtnRef}
              />
            )}
          </div>

          {/* Sources */}
          <Button
            variant="outline"
            size="sm"
            className="self-start shrink-0 !font-mono !py-1 !px-2 text-[10px] gap-1.5"
            onClick={() => setShowSourcesModal(true)}
          >
            <Plus className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Add source</span>
          </Button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-2 flex-wrap min-h-[32px]">
        {/* Active chips */}
        {platformFilter.size > 0 && (
          <FilterChip
            label="Platform"
            values={PLATFORM_OPTIONS.filter((o) => platformFilter.has(o.value)).map((o) => o.label)}
            onRemove={() => setPlatformFilter(new Set())}
          />
        )}
        {statusFilter.size > 0 && (
          <FilterChip
            label="Status"
            values={STATUS_OPTIONS.filter((o) => statusFilter.has(o.value)).map((o) => o.label)}
            onRemove={() => setStatusFilter(new Set())}
          />
        )}

        {/* Add filter — hidden once both filter types are active */}
        {activeFilterTypes.size < 2 && (
          <div className="relative">
            <button
              ref={addFilterBtnRef}
              onClick={() => {
                setAddFilterOpen((v) => !v);
                setSortOpen(false);
                setFilterPanelType(null);
              }}
              className="inline-flex items-center gap-1 text-[13px] text-[#666] hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-[#F6F6F7] border border-transparent hover:border-[#E1E3E5]"
            >
              Add filter
              <span className="text-base leading-none">+</span>
            </button>

            {addFilterOpen && (
              <AddFilterDropdown
                onSelectType={(type) => {
                  setFilterPanelType(type);
                  setAddFilterOpen(false);
                }}
                onClose={() => setAddFilterOpen(false)}
                anchorRef={addFilterBtnRef}
                activeTypes={activeFilterTypes}
              />
            )}

            {filterPanelType && (
              <FilterOptionsPanel
                type={filterPanelType}
                selectedPlatforms={platformFilter}
                selectedStatuses={statusFilter}
                onTogglePlatform={togglePlatform}
                onToggleStatus={toggleStatus}
                onClear={() => {
                  if (filterPanelType === 'platform') setPlatformFilter(new Set());
                  else setStatusFilter(new Set());
                }}
                onClose={() => setFilterPanelType(null)}
                anchorRef={addFilterBtnRef}
              />
            )}
          </div>
        )}

        {/* Clear all */}
        {(platformFilter.size > 0 || statusFilter.size > 0) && (
          <button
            onClick={() => {
              setPlatformFilter(new Set());
              setStatusFilter(new Set());
            }}
            className="text-[13px] text-[#9A9A9A] hover:text-foreground transition-colors"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <BulkActionBar
          selectedCount={selectedIds.size}
          onClearSelection={() => setSelectedIds(new Set())}
          onBulkAction={handleBulkAction}
          isLoading={bulkLoading}
          pillars={pillars}
        />
      )}

      {/* Error */}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Content */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 w-full rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-muted-foreground text-sm mb-4">
            {debouncedSearch
              ? 'No content matches your search.'
              : 'No inspiration content yet. Add competitors or RSS feeds to get started.'}
          </p>
          {!debouncedSearch && (
            <Button onClick={() => setShowSourcesModal(true)} className="gap-2">
              <Settings2 className="h-4 w-4" />
              Add Sources
            </Button>
          )}
        </div>
      ) : viewMode === 'list' ? (
        <>
          <ListView
            items={items}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onSelectAll={handleSelectAll}
            onUpdateItem={handleUpdateItem}
            onGeneratePost={handleGeneratePost}
            sortDir={sortDir}
            onToggleSortDir={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          />

          {/* Pagination */}
          {total > 0 && (
            <div className="flex flex-wrap justify-center sm:justify-end items-center gap-2 px-2 sm:px-4 py-1 mt-2">
              <span className="text-sm text-[#1A1A1A]">{lastItem} //</span>
              <span className="text-sm text-[#9A9A9A]">
                {total} ITEM{total !== 1 ? 'S' : ''}
              </span>
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="ml-4 px-3 py-2 text-sm bg-transparent disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer hover:bg-[var(--color-bg-inverse)] hover:text-[var(--color-text-inverse)] border border-[var(--border-disabled)] rounded-full"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm bg-transparent disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer hover:bg-[var(--color-bg-inverse)] hover:text-[var(--color-text-inverse)] border border-[var(--border-disabled)] rounded-full"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      ) : (
        <KanbanView
          items={items}
          onUpdateItem={handleUpdateItem}
          onGeneratePost={handleGeneratePost}
        />
      )}

      {/* Content Sources Modal */}
      {showSourcesModal && selectedHolderId && (
        <ContentSourcesModal
          holderId={selectedHolderId}
          competitors={competitors}
          rssFeeds={rssFeeds}
          onClose={() => setShowSourcesModal(false)}
          onRefresh={() => {
            void fetchSources();
            void fetchContent();
          }}
        />
      )}
    </div>
  );
}
